import React from 'react';
import { Item, Board } from '@/stores/canvasStore';
import { Pin, X, FileText, StickyNote, Film, File } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

interface PinnedSectionProps {
  pinnedItems: Item[];
  boards: Board[];
  onUnpin: (itemId: string) => void;
  onItemClick: (item: Item) => void;
}

const stickyColors: Record<string, string> = {
  yellow: 'hsl(45 93% 70%)',
  blue: 'hsl(210 85% 80%)',
  pink: 'hsl(340 85% 82%)',
  green: 'hsl(142 65% 72%)',
  gray: 'hsl(220 14% 82%)',
};

export function PinnedSection({ pinnedItems, boards, onUnpin, onItemClick }: PinnedSectionProps) {
  const isMobile = useIsMobile();
  
  if (pinnedItems.length === 0) return null;

  const getBoardForItem = (item: Item): Board | undefined => {
    return boards.find(b => b.id === item.board_id);
  };

  // Helper to get board label - shows "Home Board" for root board
  const getBoardLabel = (item: Item, board?: Board): { label: string; color: string | null } => {
    if (!item.board_id) {
      return { label: 'Not in any board', color: null };
    }
    
    if (!board) {
      return { label: 'Not in any board', color: null };
    }
    
    const isHomeBoard = board.name === 'Home' || board.parent_id === null;
    
    return {
      label: isHomeBoard ? 'Home Board' : board.name,
      color: board.icon_color || '#6366F1'
    };
  };

  const renderMiniPreview = (item: Item) => {
    const content = item.content as Record<string, unknown>;

    switch (item.item_type) {
      case 'image':
        return (
          <img
            src={content.url as string}
            alt={content.alt as string || 'Image'}
            className="w-full h-full object-cover"
          />
        );

      case 'sticky':
        const color = (content.color as string) || 'yellow';
        const bgColor = stickyColors[color] || stickyColors.yellow;
        return (
          <div
            className="w-full h-full p-3 flex items-start"
            style={{ backgroundColor: bgColor }}
          >
            <p 
              className="text-xs leading-relaxed line-clamp-3"
              style={{ color: '#1F2937' }}
            >
              {content.text as string || 'Empty note'}
            </p>
          </div>
        );

      case 'text_card':
        return (
          <div className="w-full h-full p-3 bg-white dark:bg-[#2a2a2a]">
            <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed line-clamp-4">
              {(content.body as string || '').slice(0, 100)}
            </p>
          </div>
        );

      case 'document':
        const pinnedDocBody = (content.body as string) || (content.content as string) || '';
        const pinnedDocText = pinnedDocBody.replace(/<[^>]*>/g, '');
        return (
          <div className="w-full h-full p-3 bg-white dark:bg-[#2a2a2a] flex flex-col">
            <div className="flex items-center gap-1.5 mb-1">
              <FileText className="w-3 h-3 text-primary" />
              <span className="text-xs font-medium text-gray-900 dark:text-white truncate">
                {content.title as string || 'Document'}
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
              {pinnedDocText.slice(0, 60)}
            </p>
          </div>
        );

      case 'link':
        return (
          <div className="w-full h-full bg-white dark:bg-[#2a2a2a]">
            {content.image && (
              <img 
                src={content.image as string} 
                alt={content.title as string || 'Link'}
                className="w-full h-2/3 object-cover"
              />
            )}
            <div className="p-2">
              <p className="text-xs text-gray-900 dark:text-white truncate">
                {content.title as string || content.url as string}
              </p>
            </div>
          </div>
        );

      case 'video':
        return (
          <div className="w-full h-full bg-gray-100 dark:bg-[#2a2a2a] flex items-center justify-center">
            {content.thumbnailUrl ? (
              <img 
                src={content.thumbnailUrl as string} 
                alt="Video"
                className="w-full h-full object-cover"
              />
            ) : (
              <Film className="w-6 h-6 text-gray-400" />
            )}
          </div>
        );

      case 'file':
        return (
          <div className="w-full h-full p-3 bg-gray-100 dark:bg-[#2a2a2a] flex items-center gap-2">
            <File className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            <span className="text-xs text-gray-900 dark:text-white truncate">
              {content.filename as string}
            </span>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="mb-6 mx-4 md:mx-6 lg:mx-8 bg-gray-200/80 dark:bg-[#121212] py-6 rounded-3xl">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <Pin className="w-4 h-4 text-amber-500" />
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Pinned
          </h2>
          <span className="text-xs text-gray-400 dark:text-gray-500">
            ({pinnedItems.length})
          </span>
        </div>
        
        {/* Outer container with overflow visible for buttons */}
        <div className="relative" style={{ overflow: 'visible' }}>
          {/* Scroll container with padding for X button overflow */}
          <div 
            className="flex gap-5 overflow-x-auto pb-4 pt-4 px-4 -mx-4 scrollbar-hide scroll-smooth"
            style={{ overflow: 'auto visible' }}
          >
            {pinnedItems.map(item => {
              const board = getBoardForItem(item);
              
              return (
                <div
                  key={item.id}
                  className="relative flex-shrink-0 w-56 group cursor-pointer"
                  style={{ overflow: 'visible' }}
                  onClick={() => onItemClick(item)}
                >
                  {/* Unpin button - positioned OUTSIDE, must be before card for z-index */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onUnpin(item.id);
                    }}
                    className="
                      absolute -top-3 -right-3 z-20
                      p-1.5 
                      bg-white dark:bg-gray-700 hover:bg-red-500
                      border border-gray-200 dark:border-gray-600
                      rounded-full
                      opacity-0 group-hover:opacity-100
                      transition-all duration-200
                      shadow-lg
                      hover:text-white
                    "
                    title="Unpin"
                  >
                    <X className="w-3 h-3 text-gray-700 dark:text-white" />
                  </button>
                  
                  {/* Mini card preview with 3:2 aspect ratio */}
                  <div 
                    className="rounded-xl overflow-hidden bg-white dark:bg-[#2a2a2a] shadow-sm hover:shadow-md transition-shadow"
                    style={{ aspectRatio: '3/2' }}
                  >
                    {renderMiniPreview(item)}
                  </div>
                  
                  {/* Board indicator - always show */}
                  {(() => {
                    const { label, color } = getBoardLabel(item, board);
                    return (
                      <div className="mt-2 flex items-center gap-1.5">
                        {color ? (
                          <>
                            <div 
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: color }}
                            />
                            <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {label}
                            </span>
                          </>
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-gray-500 italic truncate">
                            {label}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
            
            {/* Spacer at end */}
            <div className="flex-shrink-0 w-4" />
          </div>
          
          {/* Scroll fade indicators - softened, hidden on mobile */}
          {!isMobile && (
            <>
              <div className="absolute left-0 top-4 bottom-4 w-6 bg-gradient-to-r from-gray-200/40 dark:from-[#121212]/30 to-transparent pointer-events-none" />
              <div className="absolute right-0 top-4 bottom-4 w-6 bg-gradient-to-l from-gray-200/40 dark:from-[#121212]/30 to-transparent pointer-events-none" />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
